import { useStripe, Elements, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { PaymentRequest, Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { STRIPE_KEY } from '@/common/app/constants/keys';
import { useEffect, useState } from 'react';
import { TPayload } from '../../domain/CheckoutPage.domain';
import { getCS } from '@/common/service/storage';
import { ExternalUserValidationError } from '@/common/app/utils/errorsUtils';
import { postPay } from '@/entities/Checkout/service/postPay';
import { useCartContext, useThemeContext } from '@/common/app/contexts';
import { AnalyticsInstance } from '@/common/service/analytics';
import { createAffId } from '@/common/app/utils/affiliates';

type TStripeProps = {
    userId?: string;
    sessionId?: string;
    sendSms?: boolean;
    checkoutLoading: boolean;
    onSuccessfullyPayment: (accessKey: string) => void;
    stopTimer?: () => void;
};

const QuickPayBtn = ({
    userId,
    sessionId,
    sendSms,
    checkoutLoading,
    onSuccessfullyPayment,
    stopTimer,
}: TStripeProps) => {
    const stripe = useStripe();
    const { cartData } = useCartContext();
    const [status, setStatus] = useState<{
        canMakePayment: boolean;
        paymentRequest: PaymentRequest | null;
    }>({ canMakePayment: false, paymentRequest: null });
    const { theme } = useThemeContext();
    const themeName = theme.name === 'dark' ? 'dark' : 'light';

    // eslint-disable-next-line sonarjs/cognitive-complexity
    const initPaymentRequest = async () => {
        try {
            if (!cartData.cart_id || cartData.total <= 0 || !stripe) {
                throw new Error('Failed initialize quick pay.');
            }
            const stripePayload = {
                country: 'US',
                currency: 'usd',
                total: {
                    label: cartData.cart_id,
                    amount: cartData.total_subunits,
                },
                displayItems: cartData.cart_items.map((item) => ({
                    amount: item.total_subunits,
                    label: item.product_name,
                })),
            };

            const affiliate_id = createAffId();

            const backendPayload: TPayload = {
                special_row: false,
                session_id: sessionId || '',
                user_id: userId || '',
                send_sms: sendSms || false,
                affiliate_id,
            };

            const jamcom = getCS('jamcom');
            const jamtracker = getCS('jamtracker');

            if (jamcom && jamtracker) {
                backendPayload.jamcom = `${jamcom}`;
                backendPayload.jamtracker = `${jamtracker}`;
            }
            const newPaymentRequest = await stripe.paymentRequest(stripePayload);
            const result = await newPaymentRequest.canMakePayment();

            if (result) {
                newPaymentRequest.on('token', ({ complete, token }) => {
                    stopTimer?.();
                    AnalyticsInstance.quickPay.transactionStart();
                    postPay({ ...backendPayload, stripe_token: token.id })
                        .then((res) => {
                            if (!res.success) {
                                throw new ExternalUserValidationError(
                                    (res.errors || res.data.errors || ['Something Error']).join(
                                        ', '
                                    )
                                );
                            }

                            onSuccessfullyPayment(res.data.accesskey);

                            AnalyticsInstance.quickPay.transactionSuccessed();
                            complete('success');
                        })
                        .catch((err) => {
                            if (!(err instanceof ExternalUserValidationError)) {
                                console.log(err);
                            }
                            AnalyticsInstance.quickPay.transactionFailed();
                            complete('fail');
                        });
                });

                AnalyticsInstance.quickPay.displayButton();

                setStatus({ canMakePayment: true, paymentRequest: newPaymentRequest });
            } else {
                setStatus({ canMakePayment: false, paymentRequest: newPaymentRequest });
            }
        } catch (err) {
            setStatus({ canMakePayment: false, paymentRequest: null });
            console.log(err);
        }
    };

    useEffect(() => {
        if (stripe) {
            initPaymentRequest();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stripe]);

    if (!status.paymentRequest || checkoutLoading || !status.canMakePayment) {
        return <></>;
    }

    return (
        <div>
            <PaymentRequestButtonElement
                options={{
                    paymentRequest: status.paymentRequest,
                    style: {
                        paymentRequestButton: {
                            theme: themeName,
                        },
                    },
                }}
                className="PaymentRequestButton"
                data-label="Quick Pay"
            />
            <div style={{ marginTop: '20px' }}>Or manually enter details below</div>
        </div>
    );
};

export const QuickPayComponent = (props: TStripeProps) => {
    const [stripePromise, setStripePromise] = useState<Stripe | null>(null);

    useEffect(() => {
        (async () => {
            const stripe = await loadStripe(STRIPE_KEY);

            setStripePromise(stripe);
        })();
    }, []);

    if (!stripePromise) {
        return <></>;
    }
    return (
        <Elements stripe={stripePromise}>
            <QuickPayBtn {...props} />
        </Elements>
    );
};
